import { clamp } from 'lodash-es';
import { type IAnalyserNode, type IAudioContext } from 'standardized-audio-context';
import { v4 as uuid } from 'uuid';
import { captureFlags } from '../../configure/flags-facade';
import { type ChunkSpec, type Format } from '../../types';
import { logger } from '../lib/logger';
import { SilenceDetector } from '../lib/SilenceDetector';

const MAX_CHUNKS = 4000;
const MAX_LENGTH_ALLOWANCE_FACTOR = 1.5;

/** Raw capture output before persistence or upload. */
export interface CapturedChunk {
  id: string;
  index: number;
  blob: Blob;
  size: number;
  duration: number;
  producedAt: number;
}

interface Props {
  chunkSpec: ChunkSpec;
  format: Format;
  codec: string;
  micAnalyzer: IAnalyserNode<IAudioContext>;
  micAnalyzerId: string;
  tabAnalyzer?: IAnalyserNode<IAudioContext>;
  tabAnalyzerId?: string;
  stream: MediaStream;
  /** Emits in index order; awaited so stop() can settle at the final persist. */
  onChunk: (chunk: CapturedChunk) => Promise<void>;
  /** Capture is over; `lastIndex` is -1 when nothing was ever captured. */
  onEnded: (lastIndex: number) => void;
  onError: (errorReason: string) => void;
}

/** Emits ordered raw Blob chunks without persistence or upload concerns. */
export class ChunkCaptureRecorder {
  private mediaRecorder: MediaRecorder;
  private errorHandler: (event: Event) => void;
  public silenceDetector: SilenceDetector;
  public micAnalyzerId: string;
  public tabAnalyzerId?: string;
  private readonly useTimeslice: boolean;
  private recording: boolean = false;
  private ended = false;
  private chunkHandledIndex = 0;
  private chunkStart: number = 0;
  private rolling: boolean = false;
  private parts: Blob[] = [];
  private stopPromise: Promise<void> = Promise.resolve();
  private resolveStop: (() => void) | null = null;

  constructor(private props: Props) {
    this.useTimeslice = captureFlags.isMediaRecorderTimesliceEnabled();
    const mimeType = `audio/${props.format};codecs=${props.codec}`;
    this.mediaRecorder = new MediaRecorder(props.stream, { mimeType: mimeType });
    this.mediaRecorder.ondataavailable = this.onDataAvailable.bind(this);
    this.errorHandler = (event: Event) => {
      const message =
        event instanceof ErrorEvent && event.message ? event.message : 'Unknown error';
      this.props.onError(`MediaRecorder error: ${message}`);
    };
    this.mediaRecorder.addEventListener('error', this.errorHandler);
    this.mediaRecorder.onstop = this.finalizeChunk.bind(this);
    this.silenceDetector = new SilenceDetector(
      props.chunkSpec,
      props.micAnalyzer,
      props.tabAnalyzer
    );
    this.micAnalyzerId = props.micAnalyzerId;
    this.tabAnalyzerId = props.tabAnalyzerId;

    this.silenceDetector.onSilenceDetected(() => {
      try {
        if (this.chunkHandledIndex >= MAX_CHUNKS - 1) {
          this.props.onError('MAX_CHUNKS');
        } else {
          this.beginRollover();
        }
      } catch (err) {
        this.logRolloverFailure(err);
        throw err;
      }
    });

    this.startSlice();
    this.silenceDetector.start();
    this.recording = true;
    this.chunkStart = Date.now();
  }

  /** Persist-bound: resolves once the final chunk's handler has settled. */
  stop(): Promise<void> {
    if (!this.recording) return this.stopPromise;
    this.stopPromise = new Promise<void>((resolve) => {
      this.resolveStop = resolve;
    });
    this.silenceDetector.stop();
    this.mediaRecorder.removeEventListener('error', this.errorHandler);
    this.beginRollover();
    this.recording = false;
    return this.stopPromise;
  }

  /** Stops capture without emitting anything further (logout). */
  discard() {
    // During an in-flight stop, recording is false while ended is still false.
    // Logout must still win: drop that buffered final chunk and settle stop().
    if (!this.recording && this.ended) return;
    this.recording = false;
    this.ended = true;
    this.silenceDetector.stop();
    this.mediaRecorder.removeEventListener('error', this.errorHandler);
    this.mediaRecorder.ondataavailable = null;
    this.mediaRecorder.onstop = null;
    if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    this.parts = [];
    this.rolling = false;
    this.resolveFinalStop();
  }

  /** Rolls over buffered audio once the minimum duration is met. */
  flush(minBufferedMs: number) {
    if (!this.recording || this.rolling) return;
    // At the cap the silence path has already raised MAX_CHUNKS — a flush
    // must not mint an index past it.
    if (this.chunkHandledIndex >= MAX_CHUNKS - 1) return;
    if (Date.now() - this.chunkStart < minBufferedMs) return;
    this.beginRollover();
  }

  /** Bytes captured but not yet handed off (mid-rollover window). */
  hasPendingCapture() {
    return this.rolling || this.parts.length > 0;
  }

  private beginRollover() {
    if (this.rolling || !this.recording) return;
    this.mediaRecorder.stop();
    this.rolling = true;
  }

  private onDataAvailable({ data: blob }: BlobEvent) {
    this.parts.push(blob);
    this.beginRollover();
  }

  private async finalizeChunk(): Promise<void> {
    let finalIndex: number | null = null;
    try {
      const blobs = this.parts;
      this.parts = [];
      this.rolling = false;
      const now = Date.now();
      const duration = this.getDuration(now);
      if (blobs.length > 0) this.chunkHandledIndex++;

      if (this.recording) {
        try {
          this.silenceDetector.updateDurations(this.chunkHandledIndex);
          this.startSlice();
          this.chunkStart = now;
        } catch (err) {
          this.logRolloverFailure(err);
          this.recording = false;
          this.silenceDetector.stop();
          this.mediaRecorder.removeEventListener('error', this.errorHandler);
          this.props.onError('CHUNK_RECORDER_RESTART_FAILED');
        }
      }

      const isFinal = !this.recording;
      if (isFinal) {
        finalIndex = this.chunkHandledIndex - 1;
        this.mediaRecorder.onstop = null;
        this.mediaRecorder.ondataavailable = null;
      }

      const firstBlob = blobs[0];
      if (firstBlob) {
        const outBlob =
          blobs.length === 1 ? firstBlob : new Blob(blobs, { type: firstBlob.type });
        try {
          await this.props.onChunk({
            id: uuid(),
            index: this.chunkHandledIndex - 1,
            blob: outBlob,
            size: outBlob.size,
            duration,
            producedAt: now,
          });
        } catch (err) {
          logger.error({
            event_type: 'CHUNK_DISPATCH_FAILED',
            details: { error: err, index: this.chunkHandledIndex - 1, isFinal },
          });
        }
      } else if (isFinal) {
        logger.warn({
          event_type: 'CHUNK_RECORDER_EMPTY_FINAL_STOP',
          details: { chunkHandledIndex: this.chunkHandledIndex },
        });
      }
    } finally {
      try {
        // Publish terminal capture even if the final chunk handler rejects;
        // -1 means no audio was captured. Durable completion remains gated on
        // the corresponding chunk row settling.
        if (finalIndex !== null && !this.ended) {
          this.ended = true;
          this.props.onEnded(finalIndex);
        }
      } finally {
        if (!this.recording) this.resolveFinalStop();
      }
    }
  }

  private resolveFinalStop(): void {
    this.resolveStop?.();
    this.resolveStop = null;
  }

  private getDuration(newChunkStart: number) {
    return clamp((newChunkStart - this.chunkStart) / 1000, 0, Infinity);
  }

  private logRolloverFailure(err: unknown) {
    logger.error({
      event_type: 'CHUNK_RECORDER_ROLLOVER_FAILED',
      details: {
        error: err,
        chunkHandledIndex: this.chunkHandledIndex,
        rolling: this.rolling,
        partsBuffered: this.parts.length,
        recording: this.recording,
        mediaRecorderState: this.mediaRecorder.state,
      },
    });
  }

  private startSlice() {
    if (this.useTimeslice) {
      const maxTimesliceMs =
        Math.max(...this.props.chunkSpec.chunkDurations.map((c) => c.maxDuration)) *
        MAX_LENGTH_ALLOWANCE_FACTOR;
      this.mediaRecorder.start(maxTimesliceMs);
    } else {
      this.mediaRecorder.start();
    }
  }
}
